import { Card, Grid, Box, Typography, Stack, Chip, Divider } from '@mui/material';
import {
  TextInput,
  required,
  useTranslate,
  Toolbar,
  SaveButton,
  SimpleForm,
  SelectInput,
  BooleanInput,
  useRecordContext,
  maxLength,
  DateTimeInput,
  NumberInput,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { XButton } from '../../components/XButton';
import ButtonGroupInput from './ButtonGroupInput';
import {
  SCOPE_CHOICES,
  LEVEL_CHOICES,
  defaultDismissibleByLevel,
  BannerLevel,
} from './constants';

const DetailToolbar = ({ readOnly }: { readOnly?: boolean }) => {
  const navigate = useNavigate();
  const translate = useTranslate();

  return (
    <Toolbar sx={{ gap: 2, justifyContent: 'center' }}>
      <XButton
        onClick={() => navigate(-1)}
        label={translate('ra.action.cancel')}
        variant="outlined"
        color="inherit"
      />
      {!readOnly && <SaveButton size="small" />}
    </Toolbar>
  );
};

const LevelInput = ({ disabled }: { disabled?: boolean }) => {
  const { setValue } = useFormContext();
  return (
    <ButtonGroupInput
      source="level"
      choices={LEVEL_CHOICES}
      disabled={disabled}
      onValueChange={(level) =>
        setValue('dismissible', defaultDismissibleByLevel(level as BannerLevel))
      }
    />
  );
};

const ScopeHint = () => {
  const translate = useTranslate();
  const { watch } = useFormContext();
  const scope = watch('scope');
  const choice = SCOPE_CHOICES.find((s) => s.id === scope);
  const isGlobal = scope === 'global';

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 64 }}>
          {translate('module.announcementPopup.scope')}
        </Typography>
        <Chip
          size="small"
          label={`${scope ?? '-'} · ${
            isGlobal
              ? translate('module.announcementPopup.scopeGlobalHint')
              : translate('module.announcementPopup.scopeOnlyHint')
          }`}
          sx={{
            bgcolor: 'action.hover',
            color: choice?.color,
            fontWeight: 600,
          }}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {translate('module.announcementPopup.rulesNote')}
      </Typography>
    </Box>
  );
};

const SourceDisplay = () => {
  const translate = useTranslate();
  const { watch } = useFormContext();
  const isAuto = watch('source') === 'auto';

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Chip
        size="small"
        color="primary"
        label={translate(
          isAuto ? 'module.announcementPopup.sourceAuto' : 'module.announcementPopup.sourceManual'
        )}
        sx={{ fontWeight: 600 }}
      />
      <Typography variant="body2" color="text.secondary">
        {translate(
          isAuto
            ? 'module.announcementPopup.sourceAutoDesc'
            : 'module.announcementPopup.sourceManualDesc'
        )}
      </Typography>
    </Stack>
  );
};

const AutoMetaPanel = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  const { watch } = useFormContext();
  const isAuto = watch('source') === 'auto';
  const scope = watch('scope');

  if (!isAuto || scope === 'global') return null;

  return (
    <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {translate('module.announcementPopup.internalSource')}：{record?.venue || '-'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {translate('module.announcementPopup.updateTime')}：{record?.updatedAt || '-'}
      </Typography>
    </Box>
  );
};

export const DetailForm = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  const isAuto = record?.source === 'auto';
  const isEdit = Boolean(record?.id);

  return (
    <SimpleForm
      toolbar={<DetailToolbar readOnly={false} />}
      defaultValues={{
        scope: 'global',
        source: 'manual',
        level: 'maintenance',
        enabled: true,
        dismissible: true,
        message: { zh: '', en: '' },
        linkText: { zh: '', en: '' },
      }}
    >
      <Card sx={{ width: '100%', p: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {isEdit ? translate('module.announcementPopup.editBanner') : translate('module.announcementPopup.create')}
        </Typography>
        <TextInput
          source="code"
          label={translate('module.announcementPopup.bannerId')}
          helperText={translate('module.announcementPopup.idHint')}
          validate={isEdit ? [] : [required(), maxLength(128)]}
          disabled={isEdit || isAuto}
          fullWidth
        />
        <SelectInput
          source="scope"
          label={translate('module.announcementPopup.scope')}
          choices={SCOPE_CHOICES.map((c) => ({ id: c.id, name: translate(c.name) }))}
          validate={[required()]}
          disabled={isAuto}
          fullWidth
        />
      </Card>

      <Card sx={{ width: '100%', p: 3, mt: 2 }}>
        <Stack direction="row" spacing={3} alignItems="center">
          <Typography variant="body2" sx={{ minWidth: 64, color: 'text.secondary' }}>
            {translate('module.announcementPopup.source')}
          </Typography>
          <SourceDisplay />
        </Stack>

        <AutoMetaPanel />

        <Divider sx={{ my: 2 }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <BooleanInput
            source="enabled"
            label={translate('module.announcementPopup.enabled')}
            // disabled={isAuto}
          />
          <BooleanInput
            source="dismissible"
            label={translate('module.announcementPopup.dismissible')}
            // disabled={isAuto}
          />
        </Stack>

        <Stack direction="row" spacing={3} alignItems="center" sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ minWidth: 64, color: 'text.secondary' }}>
            {translate('module.announcementPopup.level')}
          </Typography>
          <LevelInput disabled={isAuto} />
        </Stack>

        {isEdit && (
          <NumberInput
            source="version"
            label={translate('module.announcementPopup.version')}
            helperText={translate('module.announcementPopup.versionHint')}
            // disabled={isAuto}
            sx={{ mt: 2 }}
            fullWidth
          />
        )}
      </Card>

      <Card sx={{ width: '100%', p: 3, mt: 2 }}>
        <TextInput
          source="message.zh"
          label={translate('module.announcementPopup.messageZh')}
          validate={[required(), maxLength(500)]}
          // disabled={isAuto}
          multiline
          rows={3}
          fullWidth
        />
        <TextInput
          source="message.en"
          label={translate('module.announcementPopup.messageEn')}
          validate={[required(), maxLength(500)]}
          // disabled={isAuto}
          multiline
          rows={3}
          fullWidth
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextInput
              source="linkText.zh"
              label={translate('module.announcementPopup.linkTextZh')}
              // disabled={isAuto}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              source="linkText.en"
              label={translate('module.announcementPopup.linkTextEn')}
              // disabled={isAuto}
              fullWidth
            />
          </Grid>
        </Grid>
        <TextInput
          source="linkUrl"
          label={translate('module.announcementPopup.linkUrl')}
          helperText={translate('module.announcementPopup.linkUrlHint')}
          // disabled={isAuto}
          fullWidth
        />
        {/* <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <DateTimeInput
              source="startAt"
              label={translate('module.announcementPopup.startAt')}
              disabled={isAuto}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DateTimeInput
              source="endAt"
              label={translate('module.announcementPopup.endAt')}
              disabled={isAuto}
              fullWidth
            />
          </Grid>
        </Grid> */}
      </Card>

      <Card sx={{ width: '100%', p: 3, mt: 2 }}>
        <ScopeHint />
      </Card>
    </SimpleForm>
  );
};

export default DetailForm;
